import { c as _c } from "react/compiler-runtime";
import { styled } from '@precis-digital/kurama/utils';
import { useRouter as useNextCompatRouter } from 'next/compat/router';
import Link, { type LinkProps } from 'next/link';
import { usePathname as useNextPathname, useRouter, useSearchParams } from 'next/navigation';
import { type ParsedUrlQuery } from 'querystring';
import { not } from 'ramda';
import { type PropsWithChildren } from 'react';
import { usePathname, useRouteParams } from 'shared/hooks/useRouterPath';
import { CLIENT_ID_QUERY_PARAM } from 'shared/routes';
type Push = (query: ParsedUrlQuery, reset?: boolean) => Promise<void>;
const DEFAULT_QUERY = [CLIENT_ID_QUERY_PARAM];
const getQueryParams = (query: ParsedUrlQuery): URLSearchParams => {
  return new URLSearchParams(query as Record<string, string>);
};
const useDefaultQuery = () => {
  const $ = _c(4);
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams?.get(CLIENT_ID_QUERY_PARAM);
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const clientId = t0;
  let t1;
  if ($[2] !== clientId) {
    t1 = {
      ...(clientId != null ? {
        [CLIENT_ID_QUERY_PARAM]: clientId
      } : {})
    };
    $[2] = clientId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
interface DefaultQueryLinkProps extends LinkProps {
  query?: ParsedUrlQuery;
  target?: string;
}
export const RetainDefaultQueryLink = t0 => {
  const $ = _c(15);
  let href;
  let props;
  let query;
  let target;
  if ($[0] !== t0) {
    ({
      href,
      query,
      target,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = href;
    $[2] = props;
    $[3] = query;
    $[4] = target;
  } else {
    href = $[1];
    props = $[2];
    query = $[3];
    target = $[4];
  }
  const pathname = typeof href === "object" ? href.pathname : href;
  const defaultQuery = useDefaultQuery();
  let t1;
  if ($[5] !== defaultQuery || $[6] !== query) {
    t1 = {
      ...defaultQuery,
      ...query
    };
    $[5] = defaultQuery;
    $[6] = query;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== pathname || $[9] !== t1) {
    t2 = {
      pathname,
      query: t1
    };
    $[8] = pathname;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== props || $[12] !== t2 || $[13] !== target) {
    t3 = <Link {...props} href={t2} target={target} />;
    $[11] = props;
    $[12] = t2;
    $[13] = target;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
};
export const UnStyledRetainDefaultQueryLink = styled(RetainDefaultQueryLink)<{
  disabled?: boolean;
}>(({
  disabled
}) => ({
  color: 'inherit',
  textDecoration: 'inherit',
  cursor: 'inherit',
  width: '100%',
  pointerEvents: disabled === true ? 'none' : 'auto'
}));
export const useQueryString = () => {
  const $ = _c(10);
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = useNextPathname() ?? "";
  const defaultQuery = useDefaultQuery();
  let t0;
  if ($[0] !== searchParams) {
    t0 = Object.fromEntries(searchParams?.entries() ?? []);
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const routerQuery = t0;
  let t1;
  if ($[2] !== defaultQuery || $[3] !== pathname || $[4] !== router || $[5] !== routerQuery) {
    t1 = async (query, reset) => {
      if (reset === true) {
        Object.keys(routerQuery).forEach(id => {
          if (not(DEFAULT_QUERY.includes(id)) && not(pathname?.includes(id))) {
            delete routerQuery[id];
          }
        });
      }
      const newQuery = {
        ...routerQuery,
        ...defaultQuery,
        ...query
      };
      const params = getQueryParams(newQuery);
      const queryString = Array.from(params.entries()).length > 0 ? `?${params.toString()}` : "";
      router?.replace(`${pathname}${queryString}`);
    };
    $[2] = defaultQuery;
    $[3] = pathname;
    $[4] = router;
    $[5] = routerQuery;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const pushQuery = t1;
  let t2;
  if ($[7] !== pushQuery || $[8] !== routerQuery) {
    t2 = {
      query: routerQuery,
      pushQuery
    };
    $[7] = pushQuery;
    $[8] = routerQuery;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
type Reload = () => void;
export const useReload = (): Reload => {
  return () => {
    window.location.reload();
  };
};
type GoBack = () => void;
export const useGoBack = () => {
  const $ = _c(2);
  const router = useRouter();
  let t0;
  if ($[0] !== router) {
    t0 = () => {
      router.back();
    };
    $[0] = router;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export const useChangePath = () => {
  const $ = _c(9);
  const compatRouter = useNextCompatRouter();
  const router = useRouter();
  const pathname = useNextPathname() ?? "";
  const defaultQuery = useDefaultQuery();
  const populatedPathName = pathname;
  let t0;
  if ($[0] !== compatRouter || $[1] !== defaultQuery || $[2] !== pathname || $[3] !== router) {
    t0 = async (url, query, replace) => {
      const mergedQuery = {
        ...defaultQuery,
        ...query
      };
      const newQuery = {};
      Object.entries(mergedQuery).forEach(t1 => {
        const [key, value] = t1;
        if (value != null) {
          newQuery[key] = value;
        }
      });
      if (compatRouter != null) {
        if (replace === true) {
          await compatRouter.replace({
            pathname: url ?? pathname,
            query: newQuery
          }, undefined, {
            shallow: true
          });
        } else {
          await compatRouter.push({
            pathname: url ?? pathname,
            query: newQuery
          });
        }
      } else {
        const targetPathname = url ?? pathname;
        const params = getQueryParams(newQuery);
        const queryString = Array.from(params.entries()).length > 0 ? `?${params.toString()}` : "";
        const fullUrl = `${targetPathname}${queryString}`;
        if (replace === true) {
          router.replace(fullUrl, {
            scroll: pathname !== targetPathname
          });
        } else {
          router.push(fullUrl);
        }
      }
    };
    $[0] = compatRouter;
    $[1] = defaultQuery;
    $[2] = pathname;
    $[3] = router;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const changePath = t0;
  let t1;
  if ($[5] !== changePath || $[6] !== pathname || $[7] !== populatedPathName) {
    t1 = {
      changePath,
      pathname,
      populatedPathName
    };
    $[5] = changePath;
    $[6] = pathname;
    $[7] = populatedPathName;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
};
export { usePathname, useRouteParams };
export const useCompatRouter = useNextCompatRouter;